import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client.js';

export type ParityStatus = 'data' | 'zero' | 'error';
export type ParityCategory = 'missing_sp' | 'broken_sp' | 'template' | 'timeout' | 'other';
export type ParityVerdict = 'match' | 'sage_bug' | 'upstream' | 'slow';

export interface ParityRow {
  instanceId: string;
  instanceName: string;
  templateName: string;
  customer: string;
  clientId: string;
  sp: string | null;
  status: ParityStatus;
  rowCount: number | null;
  procRowCount: number | null;
  verdict: ParityVerdict;
  durationMs: number | null;
  errorCode?: string;
  errorMessage?: string;
  category?: ParityCategory;
}

export interface ParitySnapshot {
  status: 'idle' | 'running' | 'done';
  startedAt: number | null;
  finishedAt: number | null;
  total: number;
  completed: number;
  rows: ParityRow[];
  summary: {
    total: number;
    data: number;
    zero: number;
    error: number;
    verdicts: { match: number; sage_bug: number; upstream: number; slow: number };
    byCustomer: { customer: string; total: number; data: number; zero: number; error: number }[];
    byCategory: { category: ParityCategory; count: number }[];
  };
}

/** Current parity snapshot. Auto-polls every 2s while a sweep is running. */
export function useParity() {
  const api = useApi();
  return useQuery({
    queryKey: ['cutover', 'parity'],
    queryFn: () => api.fetch<ParitySnapshot>('/cutover/parity'),
    refetchInterval: (q) => (q.state.data?.status === 'running' ? 2000 : false),
  });
}

export interface CoverageCell {
  expected: number | null;
  actual: number;
}
export interface CoverageCustomer {
  customer: string;
  templates: CoverageCell;
  reports: CoverageCell;
  parameters: { reports: number; covered: number; gaps: number };
  settings: { reports: number; configured: number };
  subscriptions: CoverageCell;
  datasources: CoverageCell;
  dataParity: {
    total: number;
    match: number;
    sage_bug: number;
    upstream: number;
    slow: number;
  } | null;
}
export interface Coverage {
  customers: CoverageCustomer[];
  /** Same shape as a customer row, with customer === "All customers". */
  totals: CoverageCustomer;
  manifestCapturedAt: string | null;
  parityRanAt: number | null;
}

/** Per-customer migration coverage matrix (templates/reports/params/…). */
export function useCoverage() {
  const api = useApi();
  return useQuery({
    queryKey: ['cutover', 'coverage'],
    queryFn: () => api.fetch<Coverage>('/cutover/coverage'),
  });
}

// ─── Certification (per-customer end-to-end validation) ──────────────────────

export type CertStatus =
  | 'PASS'
  | 'EMPTY_DEAD'
  | 'EMPTY_SUSPECT'
  | 'EMPTY'
  | 'FAIL_RENDER'
  | 'FAIL_PARITY'
  | 'FAIL_ENABLED';

export interface CertChecks {
  A_catalog: boolean;
  B_parity: boolean;
  C_render: boolean;
  C_renderJson: boolean;
  D_hasRows: boolean;
  G_disabled: boolean;
}
export interface CertDim {
  jasper: unknown;
  sage: unknown;
  match: boolean | null;
}
export interface CertParamDetail {
  name: string;
  hidden: boolean;
  jasper: string;
  sage: string;
  status: string;
}
export interface CertRecon {
  schedule: CertDim;
  delivery: CertDim;
  format: CertDim;
  recipients: {
    jasper: string[];
    sage: string[];
    missing: string[];
    extra: string[];
    match: boolean | null;
  };
  period: CertDim;
  scope: CertDim;
  params: {
    total: number;
    hidden: number;
    matched: number;
    mismatched: string[];
    cosmetic: string[];
    detail: CertParamDetail[];
  };
}
export interface CertReport {
  id: string;
  name: string;
  period: string | null;
  fmt: string;
  status: CertStatus;
  rows: number | null;
  yearRows?: number | null;
  httpJson: string;
  httpFmt: string;
  bytes: number;
  checks: CertChecks;
  recon?: CertRecon;
  matchExact?: boolean | null;
  cosmeticDiffs?: number;
  err?: string;
}
export interface CertSummary {
  customer: string;
  total: number;
  pass: number;
  emptyDead: number;
  emptySuspect: number;
  failRender: number;
  failParity: number;
  failEnabled: number;
  matchExact?: number;
  cosmeticOnly?: number;
  parityMismatch?: number;
}
export interface CertListItem {
  customer: string;
  ranAt: string;
  summary: CertSummary;
}
export interface CertDetail {
  customer: string;
  ranAt: string;
  summary: CertSummary;
  reports: CertReport[];
}

/** Every customer's latest certification summary. */
export function useCertifications() {
  const api = useApi();
  return useQuery({
    queryKey: ['cutover', 'certifications'],
    queryFn: () => api.fetch<CertListItem[]>('/cutover/certifications'),
  });
}

/** Full per-report certification detail for one customer (null → not run yet). */
export function useCertification(customer: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['cutover', 'certifications', customer],
    enabled: !!customer,
    queryFn: () =>
      api.fetch<CertDetail>(`/cutover/certifications/${encodeURIComponent(customer!)}`),
  });
}

/** Kick off a fresh full parity sweep (async; the snapshot reflects progress). */
export function useRunParity() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.fetch<{ status: string }>('/cutover/parity/run', { method: 'POST' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['cutover', 'parity'] }),
  });
}
